import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ConsentBanner } from '@iq/models';
import Footer from './components/footer/Footer';
import NavBar from './components/navBar/NavBar';
import Home from './pages/Home';
import './App.css';
import { getCart } from './services/OnlineStore';
import { getProducts, getProductById } from './services/OnlineStore';
import useCartStore from './states/CartStore';
import useProductStore from './states/ProductStore';
import { useEffect, useSyncExternalStore } from 'react';
import Cart from './pages/Cart/Cart';
import Checkout from './pages/Checkout/Checkout';
import PaymentSuccess from './pages/PaymentSuccess/PaymentSuccess';
import PaymentFailure from './pages/PaymentFailure/PaymentFailure';
import { consentManager } from './consent';

function ConsentController() {
  const consent = useSyncExternalStore(
    consentManager.subscribe,
    consentManager.getSnapshot,
    consentManager.getSnapshot
  );

  return (
    <ConsentBanner
      open={consent.isOpen}
      className="m80-consent-banner"
      manageButtonClassName="m80-consent-manage"
      preferences={consent.preferences}
      onAcceptAll={consentManager.acceptAll}
      onRejectNonEssential={consentManager.rejectNonEssential}
      onSavePreferences={consentManager.savePreferences}
      onOpen={consentManager.openPreferences}
      onClose={consentManager.closePreferences}
    />
  );
}

function CartScrollReset() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== '/cart') return;

    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo({ top: 0, behavior: 'auto' });
    document.documentElement.style.scrollBehavior = previousScrollBehavior;
  }, [location.key, location.pathname]);

  return null;
}

function App() {
  const navItems = [
    { name: 'Collection', path: '/home#top' },
    { name: 'Contact', path: '/home#contact' },
    { name: 'Cart', path: '/cart' },
  ];

  const footerDetails = ['Rochester, New York', 'Made in small runs'];

  const footerAttribution = {label: 'IQventory', href: 'https://iqventoryllc.com',};
  
  // get product, product variants, and cart. 
  // 404 on cart is NORMAL if the cart is empty for the first time
  useEffect(() => {
    getProducts()
      .then(async (data) => {
        const products = Array.isArray(data?.products) ? data.products : [];
        const ids = products.map((product) => product?.id).filter(Boolean);
        const productsWithVariants = await Promise.all(ids.map((id) => getProductById(id)));
        useProductStore.getState().setProducts(productsWithVariants);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
      });

    getCart()
      .then((cart) => {
        useCartStore.getState().setCart(cart);
      })
      .catch((error) => {
        console.error("Error fetching cart:", error);
      });
  }, []);

  const itemCount = useCartStore((state) => {
    const items = state.cart?.items ?? [];

    return items.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
  });

  return (
    <>
      <CartScrollReset />
      <NavBar navItems={navItems} navLogo="/horizontallogo.png" itemCount={itemCount} />
      <Routes>
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="/home" element={<Home />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/checkout/:paymentHashID/success" element={<PaymentSuccess />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/payment-failure" element={<PaymentFailure />} />
      </Routes>
      <Footer attribution={footerAttribution} details={footerDetails} />
      <ConsentController />
      
    </>
  );
}

export default App;
